import Highcharts from 'highcharts';
import _HighchartsReact from 'highcharts-react-official';
import { View, fromTheme, hooks, styled } from 'lib_ui-primitives';
import { createElement as rc, useMemo } from 'react';
import { useTheme } from 'styled-components';
import Chart from './Chart';
import HorizontalChartStacked from './HorizontalChartStacked';
const { useMemoizedObject } = hooks;
const HighchartsReact = _HighchartsReact.default || _HighchartsReact;
HighchartsReact.displayName = 'HighchartsReact';

const Stack = styled(View).attrs({ name: 'HorizontalChart' })`
    flex-direction: column;
    flex-grow: 1;
    flex-shrink: 1;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: ${fromTheme('charts', 'maxWidth')};
    margin: 0 auto;
    margin-top: auto;
`;

const EMPTY_ARRAY = [];

function BarChart(props) {
    const {
        data = EMPTY_ARRAY,
        hNode: { title } = {}
    } = props || { hNode: {} };

    const theme = useTheme();
    const charts = theme.charts;
    const chartColors = theme.chartColors;
    const memoizedData = useMemoizedObject(data);

    const options = useMemo(() => {
        const { disabledFontColor, defaultFontColor } = theme;
        return {
            chart: {
                type: 'bar',
                backgroundColor: 'transparent',
                styledMode: true
            },
            title: { text: '' },
            tooltip: {
                pointFormat: '{series.name}: <b>{point.y}</b>'
            },
            colors: chartColors,
            plotOptions: {
                bar: {
                    allowPointSelect: true,
                    cursor: 'pointer',
                    dataLabels: {
                        enabled: false
                    },
                    showInLegend: true,
                    borderRadius: charts.bar.borderRadius,
                    borderWidth: 0
                }
            },
            credits: { enabled: false },
            legend: {
                enabled: true,
                align: 'left',
                verticalAlign: 'bottom',
                layout: 'horizontal',
                labelFormat: '{name} - {y}',
                itemHiddenStyle: { color: disabledFontColor },
                itemHoverStyle: { color: charts.legendHoverColor },
                itemMarginTop: parseInt(charts.legend.itemSpacing, 10),
                itemDistance: parseInt(charts.legend.itemGap, 10) * 3,
                symbolHeight: parseInt(charts.legend.dotSize, 10),
                symbolWidth: parseInt(charts.legend.dotSize, 10),
                symbolRadius: parseInt(charts.legend.dotRadius, 10),
                lineHeight: parseInt(charts.legend.lineHeight, 10),
                itemStyle: {
                    color: defaultFontColor,
                    fontFamily: charts.legend.labelFont.fontFamily,
                    fontSize: charts.legend.labelFont.fontSize,
                    fontWeight: charts.legend.labelFont.fontWeight,
                    lineHeight: charts.legend.lineHeight
                }
            },
            series: [
                {
                    name: `# ${title}`,
                    colorByPoint: true,
                    showInLegend: false,
                    data: memoizedData.map(x => ({
                        name: x.name || x._id?.title || 'None',
                        y: x.y ?? x.count
                    }))
                }
            ],
            yAxis: {
                min: 0,
                title: {
                    text: ' '
                },
                allowDecimals: false,
                gridLineColor: charts.axes.gridLineColor,
                gridLineWidth: charts.axes.gridLineWidth,
                labels: {
                    style: {
                        color: charts.axes.labelColor,
                        fontFamily: charts.axes.labelFont.fontFamily,
                        fontSize: charts.axes.labelFont.fontSize
                    }
                }
            },
            xAxis: {
                categories: memoizedData.map(x => x.name || x._id?.title || 'None'),
                labels: {
                    style: {
                        color: charts.axes.labelColor,
                        fontFamily: charts.axes.labelFont.fontFamily,
                        fontSize: charts.axes.labelFont.fontSize
                    }
                }
            }
        };
    }, [memoizedData, title, theme, charts, chartColors]);

    return rc(
        Stack,
        null,
        rc(HighchartsReact, {
            highcharts: Highcharts,
            options,
            containerProps: { style: { width: '100%' } },
            height: '100%',
            width: '100%'
        })
    );
}

function HorizontalChart(props) {
    const { hNode: { stackValues = false } = {} } = props || { hNode: {} };

    if (stackValues) {
        return Chart(HorizontalChartStacked, props);
    }
    return Chart(BarChart, props);
}

export default HorizontalChart;
